// modules/website/components/SocialShareComponent.tsx - Botões de Compartilhamento Social

import React, { useState } from 'react';
import { SocialShareData } from '@/types';
import { Share2, Check, Copy } from 'lucide-react';

interface Props {
  data: SocialShareData;
  settings?: any;
}

export const SocialShareComponent: React.FC<Props> = ({ data, settings }) => {
  const {
    title,
    platforms = ['whatsapp', 'facebook', 'twitter', 'copy'],
    shareText = 'Confira essa incrível pescaria!',
    shareUrl,
    style = 'horizontal',
    showLabels = true,
    size = 'medium'
  } = data || {};

  const [copied, setCopied] = useState(false);

  // URL atual se não especificada
  const url = shareUrl || (typeof window !== 'undefined' ? window.location.href : '');

  // Configurações de tamanho
  const sizeConfig = {
    small: { icon: 20, padding: 'p-2', text: 'text-xs' },
    medium: { icon: 24, padding: 'p-3', text: 'text-sm' },
    large: { icon: 32, padding: 'p-4', text: 'text-base' }
  };

  const config = sizeConfig[size];

  // Configurações das redes sociais
  const socialConfig = {
    whatsapp: {
      name: 'WhatsApp',
      color: 'bg-green-500 hover:bg-green-600',
      icon: '📱',
      getUrl: () => `https://wa.me/?text=${encodeURIComponent(`${shareText} ${url}`)}`
    },
    facebook: {
      name: 'Facebook',
      color: 'bg-blue-600 hover:bg-blue-700',
      icon: '📘',
      getUrl: () => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
    },
    twitter: {
      name: 'Twitter',
      color: 'bg-sky-500 hover:bg-sky-600',
      icon: '🐦',
      getUrl: () => `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`
    },
    instagram: {
      name: 'Instagram',
      color: 'bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 hover:opacity-90',
      icon: '📸',
      getUrl: () => null // Instagram não tem share direto
    },
    email: {
      name: 'Email',
      color: 'bg-gray-600 hover:bg-gray-700',
      icon: '✉️',
      getUrl: () => `mailto:?subject=${encodeURIComponent(shareText)}&body=${encodeURIComponent(url)}`
    },
    copy: {
      name: 'Copiar Link',
      color: 'bg-gray-800 hover:bg-gray-900',
      icon: copied ? '✓' : '🔗',
      getUrl: () => null
    }
  };

  const handleShare = (platform: string) => {
    if (platform === 'copy') {
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      return;
    }

    if (platform === 'instagram') {
      // Instagram não tem share direto, abrir perfil ou copiar link
      navigator.clipboard.writeText(url);
      alert('Link copiado! Cole no seu Instagram.');
      return;
    }

    const shareUrl = socialConfig[platform as keyof typeof socialConfig]?.getUrl();
    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
  };

  // Classes de layout
  const layoutClasses = {
    horizontal: 'flex-row justify-center',
    vertical: 'flex-col items-center',
    floating: 'fixed right-4 top-1/2 -translate-y-1/2 flex-col z-40'
  };

  return (
    <div 
      className={style === 'floating' ? '' : 'py-8 px-6'}
      style={{ backgroundColor: style !== 'floating' ? settings?.backgroundColor : undefined }}
    >
      <div className={`${style !== 'floating' ? 'max-w-4xl mx-auto' : ''}`}>
        {/* Título */}
        {title && style !== 'floating' && (
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 text-gray-700">
              <Share2 size={20} />
              <span className="font-medium">{title}</span>
            </div>
          </div>
        )}

        {/* Botões */}
        <div className={`flex gap-3 ${layoutClasses[style]}`}>
          {platforms.map(platform => {
            const social = socialConfig[platform as keyof typeof socialConfig];
            if (!social) return null;

            return (
              <button
                key={platform}
                onClick={() => handleShare(platform)}
                className={`
                  ${config.padding} rounded-full text-white
                  ${social.color}
                  transition-all duration-200 transform hover:scale-110
                  shadow-lg hover:shadow-xl
                  flex items-center gap-2
                `}
                title={social.name}
              >
                <span style={{ fontSize: config.icon * 0.8 }}>
                  {platform === 'copy' && copied ? <Check size={config.icon} /> : social.icon}
                </span>
                {showLabels && style !== 'floating' && (
                  <span className={`${config.text} font-medium`}>
                    {platform === 'copy' && copied ? 'Copiado!' : social.name}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Prévia do texto */}
        {style !== 'floating' && shareText && (
          <p className="text-center text-gray-500 text-sm mt-4 italic">
            "{shareText}"
          </p>
        )}
      </div>
    </div>
  );
};
